import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiShare2, FiDownload, FiBookmark, FiEdit2, FiTrash2 } from 'react-icons/fi';
import { MdVerified } from 'react-icons/md';
import { postsAPI, commentsAPI, reactionsAPI, favoritesAPI } from '../../services/api';
import { CATEGORIES, REACTIONS } from '../../utils/constants';
import { useAuth } from '../../context/AuthContext';
import CommentSection from '../../components/CommentSection/CommentSection';
import toast from 'react-hot-toast';
import { resolveMediaUrl } from '../../utils/media';
import './MemePage.css';

const MemePage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [meme, setMeme] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSaved, setIsSaved] = useState(false);
  const [reactionCounts, setReactionCounts] = useState({});
  const [userReaction, setUserReaction] = useState(null);

  useEffect(() => {
    loadMeme();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const loadMeme = async () => {
    try {
      const response = await postsAPI.getById(slug);
      setMeme(response.data);
      
      const [commentsRes, reactionsRes, favRes, userReactRes] = await Promise.all([
        commentsAPI.getByPost(response.data.id),
        reactionsAPI.getByPostId(response.data.id).catch(() => ({ data: {} })),
        isAuthenticated ? favoritesAPI.check(response.data.id).catch(() => ({ data: {} })) : Promise.resolve({ data: {} }),
        isAuthenticated ? reactionsAPI.getUserReaction(response.data.id).catch(() => ({ data: {} })) : Promise.resolve({ data: {} })
      ]);
      setComments(commentsRes.data || []);

      setReactionCounts(reactionsRes.data?.reactions || {});
      setUserReaction(userReactRes?.data?.userReaction || null);
      setIsSaved(Boolean(favRes?.data?.isFavorite));
    } catch (error) {
      console.error('Error loading meme:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddComment = async (commentData) => {
    if (!isAuthenticated) {
      toast.error('Inicia sesión para comentar');
      return;
    }
    try {
      await commentsAPI.create({
        postId: meme.id,
        content: commentData.text,
        parentId: commentData.parentId
      });
      loadMeme();
      toast.success('Comentario agregado');
    } catch (error) {
      toast.error('Error al agregar comentario');
    }
  };

  const handleEditComment = async (commentId, data) => {
    try {
      await commentsAPI.update(commentId, data);
      loadMeme();
    } catch (error) {
      toast.error('Error al editar');
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await commentsAPI.delete(commentId);
      loadMeme();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleReaction = async (reactionType) => {
    if (!isAuthenticated) {
      toast.error('Inicia sesión para reaccionar');
      return;
    }
    try {
      const res = await reactionsAPI.toggle(meme.id, reactionType);
      setUserReaction(res.data?.userReaction || null);
      setReactionCounts(res.data?.reactions || {});
    } catch (error) {
      console.error('Error toggling reaction:', error);
    }
  };

  const handleSave = async () => {
    if (!isAuthenticated) {
      toast.error('Inicia sesión para guardar');
      return;
    }
    try {
      if (isSaved) {
        await favoritesAPI.remove(meme.id);
        setIsSaved(false);
        toast.success('Eliminado de guardados');
      } else {
        await favoritesAPI.add(meme.id);
        setIsSaved(true);
        toast.success('¡Guardado!');
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
      toast.error('No se pudo actualizar guardados');
    }
  };

  const handleEdit = () => {
    navigate(`/posts/edit/${meme.id}`);
  };

  const handleDelete = async () => {
    if (!window.confirm('¿Eliminar esta publicación? Esta acción no se puede deshacer.')) return;
    try {
      await postsAPI.deletePost(meme.id);
      toast.success('Publicación eliminada');
      navigate('/');
    } catch (error) {
      console.error('Error deleting post:', error);
      toast.error('No se pudo eliminar');
    }
  };

  const getCategoryInfo = (categoryId) => {
    return CATEGORIES.find(cat => cat.id === categoryId) || { name: categoryId, color: '#6366f1' };
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleShare = async () => {
    if (!isAuthenticated) {
      toast.error('Inicia sesión para compartir');
      return;
    }
    if (navigator.share) {
      try {
        await navigator.share({
          title: meme.title,
          url: window.location.href
        });
      } catch (error) {
        console.log('Share cancelled');
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success('¡Enlace copiado!');
    }
  };

  const handleDownload = () => {
    if (!isAuthenticated) {
      toast.error('Inicia sesión para descargar');
      return;
    }
    const link = document.createElement('a');
    link.href = resolveMediaUrl(meme.image);
    link.download = meme.title || 'meme';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('¡Descargando imagen!');
  };

  if (loading) {
    return (
      <div className="meme-page-loading">
        <div className="loading-spinner"></div>
        <p>Cargando meme...</p>
      </div>
    );
  }

  if (!meme) {
    return (
      <div className="meme-page-error">
        <h2>Meme no encontrado</h2>
        <Link to="/" className="back-link">Volver al inicio</Link>
      </div>
    );
  }

  const categoryInfo = getCategoryInfo(meme.category);
  const isOwner = Boolean(user?.id && meme?.author?.id && user.id === meme.author.id);
  const authorUsername = meme?.author?.username || meme?.author?.id || meme?.author_id;
  const authorIsVerified = Boolean(
    meme?.author?.verification_level && meme.author.verification_level !== 'none'
  );

  return (
    <div className="meme-page">
      {/* Header */}
      <header className="meme-header">
        <Link to="/" className="back-button">
          <FiArrowLeft />
          <span>Volver</span>
        </Link>
        <div className="header-actions">
          <button className="action-btn" onClick={handleShare}>
            <FiShare2 />
          </button>
        </div>
      </header>

      <div className="meme-container">
        {/* Meme Card */}
        <div className="meme-card">
          <div className="meme-image-container">
            <img src={resolveMediaUrl(meme.image)} alt={meme.title} className="meme-image" />
          </div>

          <div className="meme-info">
            <span className="meme-category" style={{ backgroundColor: categoryInfo.color }}>
              {categoryInfo.name}
            </span>
            
            <h1 className="meme-title">{meme.title}</h1>
            
            <div className="meme-meta">
              <span>{meme.views?.toLocaleString() || 0} visualizaciones</span>
              <span>•</span>
              <span>{formatDate(meme.created_at)}</span>
            </div>

            <div className="meme-actions">
              <button className={`action-btn-large ${isSaved ? 'active' : ''}`} onClick={handleSave}>
                <FiBookmark />
                <span>Guardar</span>
              </button>
              <button className="action-btn-large" onClick={handleShare}>
                <FiShare2 />
                <span>Compartir</span>
              </button>
              <button className="action-btn-large" onClick={handleDownload}>
                <FiDownload />
                <span>Descargar</span>
              </button>

              {isOwner && (
                <>
                  <button className="action-btn-large" onClick={handleEdit}>
                    <FiEdit2 />
                    <span>Editar</span>
                  </button>
                  <button className="action-btn-large" onClick={handleDelete}>
                    <FiTrash2 />
                    <span>Eliminar</span>
                  </button>
                </>
              )}
            </div>

            {/* Author */}
            <div className="meme-author">
              <Link to={authorUsername ? `/${authorUsername}` : '/'}>
                <img 
                  src={resolveMediaUrl(meme.author?.avatar) || `https://i.pravatar.cc/40?u=${meme.author_id}`}
                  alt={meme.author?.name}
                  className="author-avatar"
                />
              </Link>
              <div className="author-info">
                <Link to={authorUsername ? `/${authorUsername}` : '/'}>
                  <strong>
                    {meme.author?.name || 'Usuario'}
                    {authorIsVerified && (
                      <MdVerified
                        style={{
                          marginLeft: 6,
                          verticalAlign: 'middle',
                          color: meme.author.badge_color || 'currentColor'
                        }}
                        title={meme.author.badge_label || 'Cuenta verificada'}
                      />
                    )}
                  </strong>
                </Link>
                <span>Publicado el {formatDate(meme.created_at)}</span>
              </div>
            </div>

            {/* Description */}
            {meme.description && (
              <p className="meme-description">{meme.description}</p>
            )}
          </div>
        </div>

        {/* Reactions - siempre visibles (acciones requieren login) */}
        <div className="meme-reactions-bar" aria-label="Reacciones">
          {REACTIONS.map(r => (
            <button
              key={r.id}
              className={`reaction-btn ${userReaction === r.id ? 'active' : ''}`}
              onClick={() => {
                if (!isAuthenticated) {
                  toast.error('Inicia sesión para reaccionar');
                  return;
                }
                handleReaction(r.id);
              }}
              title={r.label}
              disabled={!isAuthenticated}
            >
              <span className="reaction-emoji">{r.emoji}</span>
              <span className="reaction-count">{reactionCounts[r.id] || 0}</span>
            </button>
          ))}
        </div>
        {!isAuthenticated && (
          <div className="login-prompt-reactions">
            <span>🔒 Inicia sesión para reaccionar</span>
          </div>
        )}

        {/* Comments */}
        <div className="meme-comments">
          <CommentSection
            postId={meme.id}
            comments={comments}
            onAddComment={handleAddComment}
            onEditComment={handleEditComment}
            onDeleteComment={handleDeleteComment}
            onRefresh={loadMeme}
          />
        </div>
      </div>
    </div>
  );
};

export default MemePage;
